import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { queryEncode } from 'cio/helpers/query';
import SubscriptionPageModel, { SubscriptionPageSection } from 'cio/models/subscription-page';
import SubscriptionTopicModel from 'cio/models/subscription-topic';
import AnalyticsService from 'cio/services/analytics';
import getUnsubscribedFilters from 'cio/utils/get-unsubscribed-filters';
import FormValidations from 'cio/utils/form-validations';
import { task } from 'ember-concurrency';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { inject as service } from '@ember/service';
import AlertsService from 'cio/services/alerts';
import SubscriptionCenterService from 'cio/services/subscription-center';
import AuthService from 'cio/services/auth';
import DS from 'ember-data';
import ApiErrorsService from 'cio/services/api-errors';
import RouterService from '@ember/routing/router-service';
import { LogoSource, MOCK_TOPICS, SubscriptionCenterSettings } from 'cio/utils/subscription-center';
import isEqual from 'lodash.isequal';

enum SubscriptionCenterEditModes {
  Create = 'create',
  Edit = 'edit',
}

const MAX_LENGTH = 200;

const Validations = {
  title(value: string) {
    if ((value.length ?? 0) > MAX_LENGTH) {
      return `Header can't be longer than ${MAX_LENGTH} characters`;
    }

    return true;
  },
  subtitle(value: string) {
    if ((value.length ?? 0) > MAX_LENGTH) {
      return `Headline can't be longer than ${MAX_LENGTH} characters`;
    }

    return true;
  },
};

export default class SubscriptionCenterEditController extends Controller {
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service auth!: AuthService;
  @service store!: DS.Store;
  @service apiErrors!: ApiErrorsService;
  @service router!: RouterService;

  @tracked isViewingMobile = false;
  @tracked topicBeingEdited: SubscriptionTopicModel | null = null;
  @tracked editMode: SubscriptionCenterEditModes = SubscriptionCenterEditModes.Create;
  @tracked topics: SubscriptionTopicModel[] = [];
  @tracked showTopicCongratsBanner = false;
  @tracked brandingValues: SubscriptionCenterSettings = {
    logoSource: LogoSource.None,
    logoPath: null,
    backgroundColor: null,
    foregroundColor: null,
  };
  @tracked showTopicEditModal = false;
  @tracked draggedTopicName = '';
  @tracked draggedTopicIndex = -1;
  @tracked topicDraggedOver = '';
  @tracked topicDraggedOverIndex = -1;
  @tracked updatedTopicOrder: string[] = [];
  @tracked editedTopicPrevName = '';
  @tracked isDraggingTopic = false;
  validations!: FormValidations;

  dotsImage = new URL('cio/assets/images/drag-drop/dots.svg', import.meta.url).toString();
  addFirstTopicImage = new URL('cio/assets/images/subscription-center/add-first-topic.jpg', import.meta.url).toString();

  constructor() {
    super(...arguments);

    this.set(
      'validations',
      FormValidations.create({
        validations: Validations,
        context: this,
        modelKey: 'subscriptionCenter.preferencesPage.header',
      }),
    );
  }

  resetDefaults() {
    this.isViewingMobile = false;
    this.topicBeingEdited = null;
    this.editMode = SubscriptionCenterEditModes.Create;
    this.showTopicCongratsBanner = false;
    this.brandingValues = {
      logoSource: LogoSource.None,
      logoPath: null,
      backgroundColor: null,
      foregroundColor: null,
    };
    this.updatedTopicOrder = this.subscriptionCenter.topicOrder;
    this.validations.clearErrors();
  }

  get enabledWithLastActiveTopic() {
    /*
      This is used instead of this.subscriptionCenter.enabledWithLastTopic because the active topics
      in the edit mode have not been saved yet
    */
    return this.subscriptionCenter.enabled && this.activeTopics.length === 1;
  }

  @computed(
    'subscriptionCenter.{topics.[],preferencesPage.header,topicOrder}',
    'topics.{[],@each.isDeleted,@each.hasDirtyAttributes}',
    'updatedTopicOrder',
  )
  get isDirty() {
    let changedAttributes = this.subscriptionCenter.preferencesPage?.changedAttributes();

    return (
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      !isEqual(this.subscriptionCenter.topics, [...this.topics.filter((t) => !t.isDeleted)]) ||
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      this.topics.some((topic) => ((topic.isDeleted as unknown as boolean) ? false : topic.hasDirtyAttributes)) ||
      !isEqual(
        { title: '', subtitle: '', ...(changedAttributes?.header?.[0] ?? {}) },
        { title: '', subtitle: '', ...(changedAttributes?.header?.[1] ?? {}) },
      ) ||
      !isEqual(this.subscriptionCenter.topicOrder, this.updatedTopicOrder)
    );
  }

  get savePreferencesButtonStyles() {
    return `background-color: ${this.brandingValues.backgroundColor}; border-color: ${this.brandingValues.backgroundColor}; color: ${this.brandingValues.foregroundColor};`;
  }

  @computed('topics.{[],@each.isDeleted}', 'subscriptionCenter.topics.{[],@each.isDeleted}', 'updatedTopicOrder')
  get activeTopics() {
    return (
      this.updatedTopicOrder
        .map((topicName) => this.topics.findBy('name', topicName))
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        .filter((topic) => topic != null && !topic.isDeleted)
    );
  }

  get displayTopics() {
    return this.activeTopics.length > 0 ? this.activeTopics : MOCK_TOPICS;
  }

  @action
  toggleViewingMobile(isViewing: boolean) {
    this.isViewingMobile = isViewing;
  }

  @action
  updateText(
    section: keyof SubscriptionPageModel,
    field: keyof SubscriptionPageSection,
    { target }: FlyKeyboardEvent<HTMLTextAreaElement>,
  ) {
    let currentValue = this.subscriptionCenter.preferencesPage?.[section] ?? {};
    let data = {
      ...currentValue,
      [field]: target.value,
    };

    let page = this.subscriptionCenter.preferencesPage ?? this.subscriptionCenter.createPage();
    page.set(section, data);

    this.validations.validateAttribute(field);
  }

  fetchSubscribedCustomersTask = task({ drop: true }, async () => {
    let filters = queryEncode(getUnsubscribedFilters(null, false) as Record<string, any>);
    return await this.store.query('customer', {
      filters,
    });
  });

  removeTopicNameInOrder(topicName: string) {
    let topicOrderProxy = [...this.updatedTopicOrder];
    topicOrderProxy.splice(topicOrderProxy.indexOf(topicName), 1);
    this.updatedTopicOrder = [...topicOrderProxy];
  }

  replaceTopicNameInOrder(fromIndex: number, toIndex: number, topicName: string) {
    let topicOrderProxy = [...this.updatedTopicOrder];
    topicOrderProxy.splice(fromIndex, 1);
    topicOrderProxy.splice(toIndex, 0, topicName);

    this.updatedTopicOrder = [...topicOrderProxy];
  }

  @action
  onAddTopic() {
    this.topicBeingEdited = this.store.createRecord('subscription-topic');
    this.showTopicEditModal = true;
    this.editMode = SubscriptionCenterEditModes.Create;
    this.analytics.track('subscription_center', 'add_topic_started');
  }

  @action
  editTopic(topic: SubscriptionTopicModel) {
    this.topicBeingEdited = topic;
    this.editedTopicPrevName = topic.name;
    this.showTopicEditModal = true;
    this.editMode = SubscriptionCenterEditModes.Edit;
    this.analytics.track('subscription_center', 'edit_topic_started');
  }

  @action
  onSaveEdit() {
    if (this.topicBeingEdited != null && this.topicBeingEdited.name !== this.editedTopicPrevName) {
      this.replaceTopicNameInOrder(
        this.updatedTopicOrder.indexOf(this.editedTopicPrevName),
        this.updatedTopicOrder.indexOf(this.editedTopicPrevName),
        this.topicBeingEdited.name,
      );
    }

    this.showTopicEditModal = false;
    this.topicBeingEdited = null;
    this.editedTopicPrevName = '';
  }

  @action
  onCreate(topic: SubscriptionTopicModel) {
    this.topics = [...this.topics, topic];
    this.updatedTopicOrder = [...this.updatedTopicOrder, topic.name];
    this.topicBeingEdited = null;
    this.showTopicEditModal = false;
  }

  @action
  deleteTopic(topic: SubscriptionTopicModel) {
    this.removeTopicNameInOrder(topic.name);
    topic.deleteRecord();
    // Topic not yet saved, remove it from the topics list allowing to update the ui
    if (topic.isNew as unknown as boolean) {
      this.topics.findBy('name', topic.name)?.unloadRecord();
      this.topics = this.topics.filter((t) => t.name !== topic.name);
    }
  }

  saveSubCenterTask = task({ drop: true }, async () => {
    // check if user has any topics before saving so we can congratulate them on their first topic(s)
    let hasNoTopics = this.subscriptionCenter.topics.length === 0;
    try {
      await this.subscriptionCenter.updateTopicsTask.perform(this.topics, this.subscriptionCenter.preferencesPage);
      // update topic order
      await this.subscriptionCenter.updateTopicOrderTask.perform(this.updatedTopicOrder);
    } catch (error) {
      this.subscriptionCenter.preferencesPage?.rollbackAttributes();
      this.topics.forEach((topic) => topic.rollbackAttributes());
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      this.topics = [...this.topics.filter((topic) => !topic.isDeleted)];
      this.alerts.error(
        'subscription.center.bulk-operation.failure',
        'There was an error saving your changes. Please try again.',
      );
      this.apiErrors.report(error);
      return;
    }

    await this.subscriptionCenter.fetchPages();
    await this.subscriptionCenter.fetchTopics();
    this.topics = this.subscriptionCenter.topics;
    this.updatedTopicOrder = this.subscriptionCenter.topicOrder;

    if (hasNoTopics && this.subscriptionCenter.topics.length > 0) {
      this.showTopicCongratsBanner = true;
      return;
    }
    this.alerts.success('subscription.center.bulk-save.success', 'Changes saved');
    this.router.transitionTo('workspace-settings.subscription_center.topics');
  });

  @action
  onCancel() {
    if (this.topicBeingEdited == null) {
      return;
    }

    this.topicBeingEdited.rollbackAttributes();
    this.topicBeingEdited = null;
    this.editedTopicPrevName = '';
  }

  @action
  onCloseModal() {
    this.showTopicEditModal = false;
  }

  @action
  trackSubscriptionCenterLearnMore() {}

  @action
  goToSubscriptionCenter() {
    this.router.transitionTo('workspace-settings.subscription_center.topics');
  }

  // This is used on form Submit, but also for the @onChange of the
  // fly-styled-checkbox so we need to handle both arg types
  @action
  noop(arg: Event | boolean) {
    if (typeof arg !== 'boolean') {
      arg.preventDefault();
    }

    return;
  }

  @action
  onReset() {
    this.topics.forEach((topic) => topic.rollbackAttributes());
    if (this.subscriptionCenter.preferencesPage?.isNew) {
      this.subscriptionCenter.preferencesPage.unloadRecord();
      this.subscriptionCenter.preferencesPage = null;
    } else {
      this.subscriptionCenter.preferencesPage?.rollbackAttributes();
    }
    this.resetDefaults();
  }

  onResetTopicDrag() {
    this.draggedTopicName = '';
    this.topicDraggedOver = '';
    this.isDraggingTopic = false;
    this.draggedTopicIndex = -1;
    this.topicDraggedOverIndex = -1;
  }

  @action
  onTopicDrop(event: DragEvent) {
    event.preventDefault();

    if (this.draggedTopicName !== this.topicDraggedOver) {
      this.replaceTopicNameInOrder(this.draggedTopicIndex, this.topicDraggedOverIndex, this.draggedTopicName);
    }

    this.onResetTopicDrag();
  }

  @action
  onDragTopicOver(event: DragEvent) {
    event.preventDefault();
  }

  @action
  onDragEnd(event: DragEvent) {
    event.preventDefault();

    this.onResetTopicDrag();
  }

  @action
  onTopicDropEnter(topicName: string, topicIndex: number, event: DragEvent) {
    event.preventDefault();

    this.topicDraggedOver = topicName;
    this.topicDraggedOverIndex = topicIndex;
    this.isDraggingTopic = true;
  }

  @action
  onTopicDragStarted(topicName: string, topicIndex: number) {
    this.draggedTopicName = topicName;
    this.draggedTopicIndex = topicIndex;
  }
}
